import clsx from "clsx";
import { ChangeEvent, useEffect, useState } from "react";
import ErrorMessage from "./ErrorMessage";
import { FormFieldProps } from "./FormFields.interfaces";
import styles from "./FormFields.module.scss";
import InputField from "./InputField";
import Label from "./Label";
import Select from "./Select";
import TextArea from "./TextArea";
import { getValidationRules } from "./validationRules";

const FormField = ({
  formType = "input",
  type = "text",
  optionalText = true,
  register,
  trigger,
  validation = {},
  selectValue,
  setSelectValue,
  hintText,
  name,
  errors,
  property,
  selectedIdValues,
  setSelectedIdValues,
  watch,
  disabled,
  options,
  placeholder,
  readonly = false,
  isEmptyFirstOption = true,
  onChange,
  firstOptionTextForSelectField,
  disableEmptyOption,
  showAllErrors = false,
  autoComplete,
}: FormFieldProps) => {
  const inputValue = watch(name);
  const validationRules = getValidationRules(
    validation,
    placeholder,
    options,
    watch
  );
  register(name, validationRules);
  const hasError = Boolean(errors && errors[name]);
  const describedby = hasError || hintText ? `${name}-message` : undefined;
  const [isFocused, setIsFocused] = useState(false);

  const getErrorMessage = (): string | string[] | undefined => {
    if (!errors || !errors[name]) {
      return hintText;
    }

    if (!showAllErrors) {
      return errors[name]?.message as string;
    }

    const fieldError = errors[name];
    const errorMessages: string[] = [];

    if (fieldError?.message) {
      errorMessages.push(fieldError.message as string);
    }

    // Collect additional error types if they exist
    if (fieldError?.types) {
      Object.values(fieldError.types).forEach((msg) => {
        if (msg && typeof msg === "string" && !errorMessages.includes(msg)) {
          errorMessages.push(msg);
        }
      });
    }

    return errorMessages.length > 0 ? errorMessages : hintText;
  };

  const handleOnBlur = () => {
    if (!inputValue) {
      setIsFocused(false);
    }
    // If eitherOrRequired, trigger the related field validation
    if (validation?.eitherOrRequired?.fieldName && trigger) {
      trigger([name, validation.eitherOrRequired.fieldName]);
    } else if (trigger) {
      trigger(name);
    }
  };

  const handleOnFocus = () => {
    setIsFocused(true);
  };

  // Handle onChange to set selected option id values and select value
  const handleOnChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedOptionId = event.target.selectedOptions[0].id;
    if (setSelectedIdValues) {
      setSelectedIdValues({
        ...selectedIdValues,
        [name]: selectedOptionId,
      });
    }

    if (setSelectValue) {
      setSelectValue(name, event.target.value);
    }

    onChange?.(event);

    // If eitherOrRequired, trigger the related field validation
    if (validation?.eitherOrRequired?.fieldName && trigger) {
      trigger([name, validation.eitherOrRequired.fieldName]);
    } else if (trigger) {
      trigger(name);
    }
  };

  useEffect(() => {
    if (options) {
      setIsFocused(false);
    } else {
      if (property || inputValue) {
        setIsFocused(true);
      }
    }
  }, [property, options, isFocused, inputValue, name]);

  const renderField = () => {
    switch (formType) {
      case "select":
        return (
          <Select
            {...register(name, {
              ...validationRules,
              onChange: handleOnChange,
            })}
            options={options}
            isEmptyFirstOption={isEmptyFirstOption}
            value={selectValue}
            firstOptionTextForSelectField={firstOptionTextForSelectField}
            disableEmptyOption={disableEmptyOption}
            aria-invalid={hasError}
            aria-describedby={describedby}
            aria-required={validation?.isRequired}
            autoComplete={autoComplete}
          />
        );
      case "textarea":
        return (
          <TextArea
            onFocus={handleOnFocus}
            name={name}
            aria-invalid={hasError}
            aria-describedby={describedby}
            aria-required={validation?.isRequired}
            {...register(name, validationRules)}
          />
        );
      default:
        return (
          <InputField
            type={type}
            placeholder={placeholder}
            onFocus={handleOnFocus}
            readOnly={readonly}
            disabled={disabled}
            aria-invalid={hasError}
            aria-describedby={describedby}
            aria-required={validation?.isRequired}
            autoComplete={autoComplete}
            {...register(name, validationRules)}
          />
        );
    }
  };

  return (
    <div
      onBlur={handleOnBlur}
      className={clsx(
        styles["form-row"],
        { hidden: type === "hidden" },
        isFocused && styles.focused,
        (options || type === "datetime-local" || type === "date") &&
          styles["select-wrapper"],
        hasError && styles.error,
        disabled && styles.disabled
      )}
    >
      {!options && formType === "select" ? null : (
        <>
          <Label
            htmlFor={name}
            text={placeholder}
            isOptional={optionalText && !validation?.isRequired}
            disabled={disabled}
            isRequired={validation?.isRequired}
          />

          <div className={clsx(styles["form-field"])}>
            {renderField()}
            <ErrorMessage
              id={`${name}-message`}
              message={getErrorMessage()}
              hasError={
                (!disabled && !!(errors && errors[name]?.message)) || !!hintText
              }
              isActive={!!(errors && errors[name]?.message)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default FormField;
